/**
 * Common props auto-attached to every product event.
 *
 * Card n447gxvy — OBS F1.1 trackEvent helper + commonProps + entryChannel.
 *
 * Contract
 * --------
 * - `buildCommonProps(context?)` retorna un objeto listo para merge con
 *   los `props` custom del caller. Async porque `user_id_hash` usa la
 *   Web Crypto API (SHA-256).
 * - `session_id` se resuelve una vez por pestaña. Persistido en
 *   `sessionStorage` (una pestaña = una sesión, cierre de pestaña = nueva
 *   sesión). Fallback a un UUID en memoria si `sessionStorage` está
 *   bloqueado (private mode, quota, etc.).
 * - `first-touch` (entry_channel + first_utm_*) se lee del módulo
 *   hermano `entryChannel.ts`. Sólo se adjuntan los campos presentes.
 * - `environment` y `release` provienen del build, pero **este módulo no los
 *   deriva**: los pide a `buildEnv.ts`, que es la única fuente de verdad y la
 *   que comparte con `lib/sentry.ts` (campo nativo). Card u7eQ6itN — cuando
 *   cada uno leía su propia variable, el context decía `production` en
 *   staging mientras el campo nativo decía `staging`.
 *
 * Este módulo NO decide si el evento debe emitirse — esa es la
 * responsabilidad del `trackEvent` wrapper (gate por consent).
 */
import { resolveEnvironment, resolveRelease } from './buildEnv';
import { getFirstTouch } from './entryChannel';
import { hashUserId } from './hashUserId';

/**
 * Forma del bloque de contexto que Sentry recibe como
 * `contexts.product_event`. Todos los campos opcionales se omiten cuando
 * el caller no los provee.
 */
export interface CommonProps {
  user_id_hash?: string;
  session_id: string;
  is_authenticated: boolean;
  city?: string;
  is_first_home?: boolean;
  first_utm_source?: string;
  first_utm_medium?: string;
  first_utm_campaign?: string;
  first_utm_term?: string;
  first_utm_content?: string;
  first_referrer?: string;
  first_landing_path?: string;
  entry_channel?: string;
  environment: string;
  release?: string;
}

/** sessionStorage key para el session id. */
const SESSION_KEY = 'inbest.session_id.v1';

/**
 * Devuelve un `session_id` estable por pestaña. Persiste en
 * `sessionStorage`; si el acceso falla, retorna un UUID nuevo por
 * llamada (peor: perdemos join entre eventos de la sesión, pero seguimos
 * emitiendo).
 */
function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') {
    // SSR-safe fallback — el `session_id` sólo tiene sentido en cliente.
    return generateUuid();
  }
  try {
    let sid = window.sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = generateUuid();
      window.sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch {
    return generateUuid();
  }
}

/**
 * `crypto.randomUUID()` sin depender del namespace global directamente,
 * con fallback a un UUID v4 pseudo-random si no está disponible (jsdom
 * viejo, workers con crypto restringido).
 */
function generateUuid(): string {
  const g = (globalThis as { crypto?: Crypto }).crypto;
  if (g && typeof g.randomUUID === 'function') {
    return g.randomUUID();
  }
  // Fallback (no criptográficamente seguro, sólo para no crashear en
  // entornos exóticos — la unicidad práctica es suficiente para telemetría).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Ensambla los `common_props` para un evento. Async porque el hash del
 * user id usa Web Crypto. Cuando `userId` es undefined:
 *   - `is_authenticated=false`
 *   - `user_id_hash` se omite (no queda como `undefined` en el payload).
 */
export async function buildCommonProps(context?: {
  userId?: string;
  city?: string;
  isFirstHome?: boolean;
}): Promise<CommonProps> {
  const firstTouch = getFirstTouch();

  // Mismos resolvers que `lib/sentry.ts` — el context NO puede contradecir al
  // campo nativo. Card u7eQ6itN.
  const environment = resolveEnvironment();
  const release = resolveRelease();

  const props: CommonProps = {
    session_id: getOrCreateSessionId(),
    is_authenticated: Boolean(context?.userId),
    environment,
  };

  if (release) props.release = release;
  if (context?.city !== undefined) props.city = context.city;
  if (context?.isFirstHome !== undefined) {
    props.is_first_home = context.isFirstHome;
  }

  if (firstTouch) {
    if (firstTouch.entry_channel) props.entry_channel = firstTouch.entry_channel;
    if (firstTouch.first_utm_source)
      props.first_utm_source = firstTouch.first_utm_source;
    if (firstTouch.first_utm_medium)
      props.first_utm_medium = firstTouch.first_utm_medium;
    if (firstTouch.first_utm_campaign)
      props.first_utm_campaign = firstTouch.first_utm_campaign;
    if (firstTouch.first_utm_term)
      props.first_utm_term = firstTouch.first_utm_term;
    if (firstTouch.first_utm_content)
      props.first_utm_content = firstTouch.first_utm_content;
    if (firstTouch.first_referrer)
      props.first_referrer = firstTouch.first_referrer;
    if (firstTouch.first_landing_path)
      props.first_landing_path = firstTouch.first_landing_path;
  }

  if (context?.userId) {
    try {
      props.user_id_hash = await hashUserId(context.userId);
    } catch {
      // Si el hash falla (Web Crypto no disponible), preferimos emitir
      // el evento sin `user_id_hash` a fallar. `is_authenticated` sigue
      // reflejando la realidad.
    }
  }

  return props;
}
